import { useEffect, useMemo, useState } from "react";
import { Paper, Typography } from "@mui/material";

import Page from "./Page";
import useData from "../../hooks/useData";
import ClientPortalOverviewModule from "../clientPortal/modules/ClientPortalOverviewModule";
import ClientPortalImageGalleryModule from "../clientPortal/modules/ClientPortalImageGalleryModule";
import ClientPortalInventoryReportsModule from "../clientPortal/modules/ClientPortalInventoryReportsModule";

const moduleRegistry = {
  imageGallery: ClientPortalImageGalleryModule,
  clientPortalOverview: ClientPortalOverviewModule,
  inventoryReports: ClientPortalInventoryReportsModule,
};

const resolveModuleRegistryKey = (moduleKey = "") => {
  const trimmed = String(moduleKey || "").trim();
  if (!trimmed) return "";

  // Fast path for exact keys
  if (moduleRegistry[trimmed]) return trimmed;

  // Normalize loose user-entered keys from catalog (spacing/case/hyphen/underscore)
  const canonical = trimmed.toLowerCase().replace(/[\s_-]+/g, "");
  const aliases = {
    imagegallery: "imageGallery",
    imagegalley: "imageGallery",
    clientportaloverview: "clientPortalOverview",
    overview: "clientPortalOverview",
    inventoryreports: "inventoryReports",
    inventoryreport: "inventoryReports",
  };

  return aliases[canonical] || "";
};

const ClientUserDashboard = () => {
  const { executeAction } = useData();
  const [clientId, setClientId] = useState(null);
  const [modules, setModules] = useState([]);
  const [activePanel, setActivePanel] = useState("");

  const handleToolBarClick = (item) => {
    setActivePanel(item);
  };

  useEffect(() => {
    const loadModules = async () => {
      const result = await executeAction({
        entityName: "Client",
        action: "getMyPortalModules",
      });

      if (result?.isSuccessful) {
        const loadedModules = Array.isArray(result?.data?.modules)
          ? result.data.modules
          : [];

        setClientId(result?.data?.clientId || null);
        setModules(loadedModules);

        if (loadedModules.length > 0) {
          setActivePanel(loadedModules[0].moduleKey);
        }
      } else {
        setClientId(null);
        setModules([]);
        setActivePanel("");
      }
    };

    loadModules();
  }, []);

  const toolbarItems = useMemo(
    () =>
      modules.map((module) => ({
        name: module.moduleKey,
        label: module.name || module.moduleKey,
        iconName: "Widgets",
      })),
    [modules],
  );

  const activeModule = modules.find(
    (module) => module.moduleKey === activePanel,
  );
  const activeModuleRegistryKey = resolveModuleRegistryKey(
    activeModule?.moduleKey,
  );
  const ActiveComponent =
    activeModule && moduleRegistry[activeModuleRegistryKey]
      ? moduleRegistry[activeModuleRegistryKey]
      : null;

  return (
    <Page
      pageName="clientDashboard"
      includeToolbar={toolbarItems.length > 0}
      onToolItemClick={handleToolBarClick}
      items={toolbarItems}
    >
      {modules.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Your client portal is not configured yet. Please contact support.
          </Typography>
        </Paper>
      ) : ActiveComponent ? (
        <ActiveComponent
          clientId={clientId}
          preset={activeModule?.preset || {}}
        />
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Module component is not registered yet.
          </Typography>
        </Paper>
      )}
    </Page>
  );
};

export default ClientUserDashboard;
